import { useState, useCallback, useMemo } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../utils/filterUtils';
import {
    useGetBlockShelfSeqItemDescriptionsQuery,
    useCreateBlockShelfSeqItemDescriptionMutation,
    useUpdateBlockShelfSeqItemDescriptionMutation,
    useDeleteBlockShelfSeqItemDescriptionMutation
} from '../../../api/endpoints/itemDescriptions';
import toastService from '../../../utils/toastService';

const EMPTY_ITEMS = [];

const INITIAL_FILTER_STATE = {
    partCode: null,
    partCodeMatchMode: FilterMatchMode.CONTAINS,

    partDesc: null,
    partDescMatchMode: FilterMatchMode.CONTAINS,

    block: null,
    blockMatchMode: FilterMatchMode.CONTAINS,

    shelf: null,
    shelfMatchMode: FilterMatchMode.CONTAINS,

    sequence: null,
    sequenceMatchMode: FilterMatchMode.CONTAINS,

    eyes: null,
    eyesMatchMode: FilterMatchMode.CONTAINS,

    partBrand: null,
    partBrandMatchMode: FilterMatchMode.CONTAINS
};

const backendColumnMap = {
    partCode: 'PartCode',
    partDesc: 'PartDesc',
    block: 'Block',
    shelf: 'Shelf',
    sequence: 'Sequence',
    eyes: 'Eyes',
    partBrand: 'PartBrand'
};

const TEXT_FIELDS = ['partCode', 'partDesc', 'block', 'shelf', 'sequence', 'eyes', 'partBrand'];

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    TEXT_FIELDS.forEach((field) => {
        const value = filtersState?.[field];
        const matchMode = filtersState?.[`${field}MatchMode`];
        const column = backendColumnMap[field];

        if (!column || value === null || value === undefined || value === '') return;

        filters.push({
            column,
            operator: matchModeToOperator[matchMode] ?? 1,
            value: String(value).trim()
        });
    });

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;

    const column = backendColumnMap[field];

    if (!column) return null;

    return [
        {
            column,
            direction: order === 1 ? 1 : 2
        }
    ];
};

const useBlockShelfSeqItemDescription = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);

    const languageId = getCurrentLanguageId?.() ?? 2;

    const queryArgs = useMemo(() => {
        const filters = buildDynamicFilters(activeFilters);
        const sorts = buildDynamicSorts(sortField, sortOrder);

        return {
            isActive: 1,
            pageNumber: page,
            pageSize,
            languageId,
            filters: filters.length > 0 ? filters : null,
            sorts
        };
    }, [page, pageSize, languageId, activeFilters, sortField, sortOrder]);

    const exportParams = useMemo(() => {
        const filters = buildDynamicFilters(activeFilters);
        const sorts = buildDynamicSorts(sortField, sortOrder);

        return {
            filters: filters.length > 0 ? filters : [],
            sorts: sorts ?? [],
            compressionType: 0
        };
    }, [activeFilters, sortField, sortOrder]);

    const { data, isLoading, isFetching, refetch } =
        useGetBlockShelfSeqItemDescriptionsQuery(queryArgs);

    const [createItem, { isLoading: isCreating }] =
        useCreateBlockShelfSeqItemDescriptionMutation();

    const [updateItem, { isLoading: isUpdating }] =
        useUpdateBlockShelfSeqItemDescriptionMutation();

    const [deleteItem, { isLoading: isDeleting }] =
        useDeleteBlockShelfSeqItemDescriptionMutation();

    const items = data?.items ?? EMPTY_ITEMS;
    const totalRecords = Number(data?.totalCount ?? 0);

    const handlePage = useCallback((event) => {
        setPage(event.page + 1);
        setPageSize(event.rows);
    }, []);

    const handleSortChange = useCallback((event) => {
        setSortField(event?.sortField || null);
        setSortOrder(event?.sortOrder || 0);
        setPage(1);
    }, []);

    const handleApplyFilters = useCallback((filters) => {
        setActiveFilters(filters);
        setPage(1);
    }, []);

    const handleResetFilters = useCallback(() => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(null);
        setSortOrder(0);
        setPage(1);
    }, []);

    const handleNew = useCallback(() => {
        setSelectedItem(null);
        setDialogVisible(true);
    }, []);

    const handleEdit = useCallback((rowData) => {
        setSelectedItem(rowData);
        setDialogVisible(true);
    }, []);

    const handleSave = useCallback(
        async (formData) => {
            try {
                if (formData.id) {
                    await updateItem(formData).unwrap();
                    toastService.success(
                        th('blockShelfSeqItemDescription.toast.updateSuccess') ||
                            'Kalem başarıyla güncellendi'
                    );
                } else {
                    await createItem(formData).unwrap();
                    toastService.success(
                        th('blockShelfSeqItemDescription.toast.createSuccess') ||
                            'Kalem başarıyla oluşturuldu'
                    );
                }

                await refetch();
                setDialogVisible(false);
                setSelectedItem(null);
            } catch (error) {
                console.error('Save error:', error);
                toastService.error(
                    th('blockShelfSeqItemDescription.toast.saveError') ||
                        'Kayıt sırasında bir hata oluştu'
                );
                throw error;
            }
        },
        [createItem, updateItem, refetch, th]
    );

    const handleDelete = useCallback(
        async (item) => {
            try {
                await deleteItem({ id: item.id }).unwrap();

                toastService.success(
                    th('blockShelfSeqItemDescription.toast.deleteSuccess') ||
                        'Kalem başarıyla silindi'
                );

                await refetch();
                setDialogVisible(false);
                setSelectedItem(null);
            } catch (error) {
                console.error('Delete error:', error);
                toastService.error(
                    th('blockShelfSeqItemDescription.toast.deleteError') ||
                        'Silme sırasında bir hata oluştu'
                );
            }
        },
        [deleteItem, refetch, th]
    );

    const handleCloseDialog = useCallback(() => {
        setDialogVisible(false);
        setSelectedItem(null);
    }, []);

    return {
        items,
        totalRecords,
        isLoading: isLoading || isFetching,
        isCreating,
        isUpdating,
        isDeleting,
        dialogVisible,
        selectedItem,
        page,
        pageSize,
        sortField,
        sortOrder,
        activeFilters,
        handlePage,
        onSort: handleSortChange,
        handleApplyFilters,
        handleResetFilters,
        handleNew,
        handleEdit,
        handleSave,
        handleDelete,
        handleCloseDialog,
        exportParams
    };
};

export default useBlockShelfSeqItemDescription;